import {bindable, customElement, inject} from 'aurelia-framework';
import {EventAggregator} from 'aurelia-event-aggregator';
import {BindingSignaler} from 'aurelia-templating-resources';
import * as _ from 'lodash';

import {GoogleMapsService} from './components/google-maps-service';
import {EventDataService} from './components/event-data-service';
import {applyStyle} from './components/map-styling';
import './humboldt-event-planner.less';
import {BussMailPlannerService} from './components/bus-mail-planner-service';
import {UnsavedChanges} from '../../../form/unsaved-changes-prevent';

const BUS_MAIL_MODULE = 'bus-mail-module';
const BUS_MODULE = 'bus-module';

@customElement('humboldt-event-planner')
@inject(
    GoogleMapsService,
    EventDataService,
    BussMailPlannerService,
    EventAggregator,
    BindingSignaler
)
export class HumboldtEventPlanner {
    @bindable object;
    @bindable readOnly;
    @bindable identifier;

    plannerForm;
    pinEditForm;

    plannablePostalCodes = [];
    planningPotentials = [];

    plannablePostalPlaceIds = {};
    filterPostalCodes = [];
    filterSelectedPlaceIds = {};
    filterAllPlaceIds = {};

    selectedPostalCodes = [];

    lastInteractedFeatureIds = [];
    lastClickedFeatureIds = [];
    currentLocation = null;

    ONLINE_TRAVEL_EVENING = 'online-travel-evening';

    mapHoverInfo = null;

    subscribers = [];

    constructor(
        googleMaps,
        eventData,
        busMailPlanner,
        ea,
        signaler
    ) {
        this.googleMaps = googleMaps;
        this.eventData = eventData;
        this.busMailPlanner = busMailPlanner;
        this.ea = ea;
        this.signaler = signaler;
        this.handlePinEditSubmit = this.handlePinEditSubmit.bind(this);
        this.messages = [];
        this.busMailModuleIdentifier = BUS_MAIL_MODULE;
        this.busModuleIdentifier = BUS_MODULE;
        // function to restore native StructuredClone

        (function () {
            const nativeStructuredClone = window.structuredClone;
            if (!nativeStructuredClone) return;
            window.structuredClone = function (obj, options) {
                if (!obj || !this) {
                    return obj;
                }
                return nativeStructuredClone.call(this, obj, options);
            };
            try {
                Object.defineProperty(window, 'structuredClone', {
                    configurable: true,
                    writable: true,
                });
            } catch (e) {
            }
        })();
    }

    detached() {
        if (this.googleMaps) {
            this.googleMaps.clearMarkers();
        }

        this.subscribers.forEach(subscriber => {
            subscriber.dispose();
        })

        UnsavedChanges.ignore = false;
    }

    async attached() {
        UnsavedChanges.ignore = true;
        try {
            const settings = await this.eventData.getApiSettings();
            await this.googleMaps.initialize(
                settings.apiKey,
                settings.mapId,
                settings.language,
                this.handleFeatureLayerStyle.bind(this)
            );
            this.setupSubscriptions();
            await this.initializeLocalVars();
        } catch (error) {
            console.error('Error during component initialization:', error);
        }
    }

    async initializeLocalVars() {
        this.selectedPostalCodes = this.object?.selectedPostalCodes ?? [];
    }

    setupSubscriptions() {
        this.subscribers.push(
            this.ea.subscribe('sio_dynamic_config_change', ({form, configParams}) => {

                if (form?.formService?.config?.id !== 'update-mailing-bus-stops') {
                    this.handleFormValueChange(form, null, configParams);
                }
            })
        );
    }

    async handleFormValueChange(form, field, configParams) {
        const value = form.formService.getValue();

        console.log('FORM VALUE CHANGED', form.formService, field, configParams);

        let plannerData = form.formService.getFieldByProperty(
            'addressPotential'
        )?.options ?? {};

        //Potentials which COULD be planned
        this.plannablePostalCodes = plannerData?.plannablePostalCodes ?? [];
        //Potentials which are planned right now
        this.planningPotentials = plannerData?.planningPotentials ?? [];

        // Prepare derived values
        this.plannablePostalPlaceIds = Object.fromEntries(
            (plannerData?.plannablePostalCodes ?? [])
                .filter(e => e.googlePlaceId)
                .map((e) => [e.googlePlaceId, {mapsHover: e.mapsHover, zip: e.zip}])
        );
        this.filterPostalCodes = _.uniq(
            (plannerData?.planningPotentials ?? [])
                .filter(e => e.zip && e.selected)
                .map((e) => e.zip)
        );
        this.filterSelectedPlaceIds = Object.fromEntries(
            (plannerData?.planningPotentials ?? [])
                .filter(e => e.googlePlaceId && e.selected)
                .map((e) => [e.googlePlaceId, 1])
        );
        this.filterAllPlaceIds = Object.fromEntries(
            (plannerData?.planningPotentials ?? [])
                .filter(e => e.googlePlaceId && !e.selected)
                .map((e) => [e.googlePlaceId, 1])
        );

        let hasLocation = false;

        if ((plannerData?.eventLocations ?? []).length > 0) {

            this.googleMaps.clearMarkers();


            (plannerData?.eventLocations ?? []).forEach((location) => {

                this.googleMaps.addAdvancedMarkerToMap(
                    location,
                    (ref) => this.onMarkerClick(ref),
                    (ref, hover) => this.onMarkerHover(ref, hover),
                );

                if (location.selected) {
                    this.drawResizableCircle(value?.radius, location);
                    hasLocation = true;
                }
            });
        } else if (this.identifier == null) {
            // Check only for planner 1 we clear
            this.googleMaps.clearMarkers();
        }

        if (!hasLocation) {
            this.drawResizableCircle(value?.radius, null);
        }

        // If travel agencies change, we preselect all postal codes from this agency
        if (
            configParams?.changedField === 'travelAgencies' ||
            configParams?.changedField === 'fromDate' ||
            configParams?.changedField === 'toDate' ||
            configParams?.changedField === 'radius'
        ) {
            this.selectedPostalCodes = this.filterPostalCodes;

            const field = this.plannerForm?.formService?.getFieldByProperty(
                'selectedPostalCodes'
            );
            if (field) {
                field.setValue(this.selectedPostalCodes);
            }
        }

        if (
            configParams?.initialLoad ||
            configParams?.changedField === 'travelAgencies' ||
            configParams?.changedField === 'eventLocation' ||
            configParams?.changedField === 'fromDate' ||
            configParams?.changedField === 'toDate' ||
            configParams?.changedField === 'radius'
        ) {

            let centerRect = plannerData?.displayViewport;

            if (centerRect) {
                this.googleMaps.fitBounds(centerRect);
            }
        }

        this.googleMaps.setFeatureLayerStyle(this.getCombinedStyle.bind(this));
    }

    async handlePinEditSubmit() {
        this.busMailPlanner.handlePinEditSubmit(
            this.pinEditForm,
            this.object,
            this.googleMaps
        );

        const field = this.plannerForm?.formService?.getFieldByProperty(
            'busstops'
        );

        if (field) {
            this.busMailPlanner.selectedPin = [];
            field.setValue([]);
            this.plannerForm.formValueChanged(field);
        }
    }

    onMarkerHover(location, hover) {
        this.pinHoverInfo = hover ? location.pinHover : null;
    }

    async onMarkerClick(location) {
        if (location.modelId === 'tourism-event/location') {

            const field = this.plannerForm?.formService?.getFieldByProperty(
                'eventLocation'
            );

            if (field) {
                field.setValue({id: location.id, modelId: location.modelId});
                this.plannerForm.formValueChanged(field);
            }

        } else if (this.identifier === BUS_MAIL_MODULE && location.modelId === 'tourism-bus/busstop') {

            const field = this.plannerForm?.formService?.getFieldByProperty(
                'busstops'
            );

            if (field) {
                let indexOf = this.busMailPlanner.selectedPin.map((e) => e.id).indexOf(location.id);

                if (indexOf > -1) {
                    this.busMailPlanner.selectedPin.splice(indexOf, 1);
                } else {
                    this.busMailPlanner.selectedPin.push(location);
                }

                field.setValue(this.busMailPlanner.selectedPin.map((e) => {
                    return {id: e.id, modelId: e.modelId};
                }));
                this.plannerForm.formValueChanged(field);

                this.handleFormValueChange(this.plannerForm, null, {});
            }
        }
    }

    toggleAddressPotential(event, row) {
        if (event.target.checked) {
            this.selectedPostalCodes.push(row.zip);
        } else {
            const index = this.selectedPostalCodes.indexOf(row.zip);
            if (index !== -1) {
                this.selectedPostalCodes.splice(index, 1);
            }
        }

        this.updateFormAndMap();
    }

    handleFeatureLayerStyle(type, data, event) {
        if (type === 'click' && this.identifier !== BUS_MODULE) {
            data.forEach((placeId) => {

                let postalCode = this.plannablePostalPlaceIds?.[placeId]?.zip;

                if (!postalCode) {
                    return;
                }

                const index = this.selectedPostalCodes.indexOf(postalCode);
                if (index !== -1) {
                    this.selectedPostalCodes.splice(index, 1);
                    this.lastClickedFeatureIds.splice(
                        this.lastClickedFeatureIds.indexOf(placeId),
                        1
                    );

                } else {
                    this.selectedPostalCodes.push(postalCode);
                    this.lastClickedFeatureIds.push(placeId);
                }
            });
            this.updateFormAndMap();
        } else if (type === 'mousemove') {
            this.lastInteractedFeatureIds = data;

            if (data != null && data.length > 0) {
                this.mapHoverInfo = this.plannablePostalPlaceIds?.[data[0]]?.mapsHover;
            } else {
                this.mapHoverInfo = null;
            }

        } else if (type === 'mouseout') {
            this.lastInteractedFeatureIds = [];
            this.mapHoverInfo = null;
        }
        this.googleMaps.setFeatureLayerStyle(this.getCombinedStyle.bind(this));
    }

    getCombinedStyle(params) {
        return applyStyle(
            params,
            this.lastClickedFeatureIds,
            this.filterSelectedPlaceIds,
            this.filterAllPlaceIds,
            this.lastInteractedFeatureIds,
            this.plannablePostalPlaceIds,
            this.identifier
        );
    }

    async centerPostalCode(googlePlaceId) {
        let location = await this.googleMaps.getRectForPlaceId(googlePlaceId);

        this.googleMaps.map.fitBounds(location);
        var zoom = this.googleMaps.map.getZoom();
        this.googleMaps.map.setZoom(zoom > 11 ? 11 : zoom);
    }

    drawResizableCircle(radius, location) {
        if (!location?.address?.coordinates || !radius || radius == 0) {
            this.googleMaps.mapCircle?.setMap(null);
            return;
        }
        this.googleMaps.drawCircle(
            location.address.coordinates,
            radius,
            this.handleRadiusChange.bind(this)
        );
        //this.updatePostalCodesInCircle(radius);
    }

    handleRadiusChange(newRadius) {
        this.plannerForm.formService
            .getFieldByProperty('radius')
            .setValue(newRadius);
        this.updatePostalCodesInCircle(newRadius);
    }

    async updatePostalCodesInCircle(radius) {
        if (!this.currentLocation?.address?.coordinates) return;

        this.selectedPostalCodes = [];
        this.lastClickedFeatureIds = [];

        const points = this.generateRandomPointsInCircle(
            this.currentLocation.address.coordinates,
            radius
        );
        const geocodePromises = points.map((point) =>
            this.googleMaps.reverseGeocode(point).catch(() => null)
        );
        const results = await Promise.all(geocodePromises);

        const uniqueResults = new Map();
        results.forEach((res) => {
            if (res?.postalCode) {
                uniqueResults.set(res.postalCode, res.placeId);
            }
        });

        this.selectedPostalCodes = [...uniqueResults.keys()];
        this.lastClickedFeatureIds = [...uniqueResults.values()];

        this.updateFormAndMap();
    }

    generateRandomPointsInCircle(center, radiusInKm, numPoints = 100) {
        const points = [];
        for (let i = 0; i < numPoints; i++) {
            const randomRadius = Math.sqrt(Math.random()) * radiusInKm;
            const randomAngle = Math.random() * 2 * Math.PI;

            const latOffset = (randomRadius / 111) * Math.cos(randomAngle);
            const lngOffset =
                (randomRadius /
                    (111 * Math.cos(center.lat * (Math.PI / 180)))) *
                Math.sin(randomAngle);

            points.push({
                lat: center.lat + latOffset,
                lng: center.lng + lngOffset,
            });
        }
        points.push(center); // Ensure the center point is included
        return points;
    }

    updateFormAndMap() {
        const field = this.plannerForm?.formService?.getFieldByProperty(
            'selectedPostalCodes'
        );
        if (field) {
            field.setValue(this.selectedPostalCodes);
            this.plannerForm.formValueChanged(field);
        }
        this.googleMaps.setFeatureLayerStyle(this.getCombinedStyle.bind(this));
    }

    clearForm() {
        if (this.plannerForm && this.plannerForm.formService) {
            this.plannerForm.formService.config.fields.forEach((field) =>
                field.resetValue()
            );
            this.plannerForm.formService
                .getFieldByProperty('exclusivePlanning')
                ?.setValue('no');
            this.plannerForm.formValueChanged(
                this.plannerForm.formService.getFieldByProperty(
                    'exclusivePlanning'
                )
            );
        }

        this.googleMaps.clearMarkers();

        this.plannablePostalPlaceIds = {};
        this.filterPostalCodes = [];
        this.filterSelectedPlaceIds = {};
        this.filterAllPlaceIds = {};

        this.selectedPostalCodes = [];
        this.lastClickedFeatureIds = [];
        this.currentLocation = null;
        this.googleMaps.mapCircle?.setMap(null);
        this.googleMaps.setFeatureLayerStyle(this.getCombinedStyle.bind(this));
    }
}
